import type React from "react";
import { type ChangeEvent, type FC, useCallback, useEffect, useRef, useState } from "react";
import { CommodityApi } from "@/api/CommodityApi";
import { StyledButton } from "@/components/Button";
import InputLabel from "@/components/Common/InputLabel/InputLabel";
import Select from "@/components/Common/Select/Select";
import StyledInput from "@/components/Common/StyledInput/StyledInput";
import {
	ITEM_DESCRIPTION_FIELD_LIMIT,
	NUMERIC_FIELD_LIMIT,
	VENDOR_NAME_FIELD_LIMIT,
} from "@/constants";
import type { Option } from "@/interfaces/Option";
import type QueryParams from "@/interfaces/QueryParams";
import { SEARCH_LABELS } from "@/utils/Enum";
import { multiSelectCountryRegion } from "@/utils/MultiSelectCountryRegion";
import { validateNumericInput, validateStringInput } from "@/utils/Validation";
import "@/index.css";

const searchButtonStyle = "mt-6 w-[20vw] max-w-[150px] text-white";
const clearAllButtonStyle = "mt-6 w-[20vw] max-w-[150px]";
const commonInputStyle =
	"box-border pl-2 w-full min-w-0 min-w-[80px] basis-full sm:basis-1/2 md:basis-1/6 flex-1 min-[1662px]:flex-none min-[1662px]:w-[250px]";
const commonGrowInputStyle =
	"box-border pl-2 w-full min-w-0 min-w-[80px] basis-full sm:basis-1/2 md:basis-1/6 min-[1662px]:flex-1";
const commonDropdownStyle =
	"box-border w-full min-w-0 min-w-[80px] basis-full sm:basis-1/2 md:basis-1/6 flex-1 min-[1662px]:flex-none min-[1662px]:w-[250px]";
const SellingCountryDropdownStyle =
	"box-border w-full min-w-[80px] flex-1 sm:basis-1/2 md:basis-1/6 min-[1662px]:flex-none min-[1662px]:w-[250px] common-dropdown-fixed";

export interface SearchBarProps {
	searchParams: Partial<QueryParams>;
	updateSearchParams: (value: Partial<QueryParams>) => void;
	handleSearchClick: (params?: Partial<QueryParams>) => void;
	searchCardExpanded?: (enabled: boolean) => void;
	shouldKeepExpanded?: boolean;
	onSearchParamsChange?: (params: Partial<QueryParams>, commodity: string[]) => void;
	onDoSearchChange?: (doSearchFn: () => void) => void;
}

const SearchForm: FC<SearchBarProps> = ({
	searchParams,
	updateSearchParams,
	handleSearchClick,
	searchCardExpanded, // Optional callback to notify parent about search card state
	shouldKeepExpanded = false,
	onDoSearchChange, // Optional callback to provide doSearch function to parent
}) => {
	const [localSearchParams, setLocalSearchParams] = useState<Partial<QueryParams>>(searchParams);

	const [commodityList, setCommodityList] = useState<Option[]>([]);
	const [commoditySelect, setCommoditySelect] = useState<string[]>([]);
	const [isSearchButtonEnabled, setIsSearchButtonEnabled] = useState<boolean>(false);
	const [isLoading, setIsLoading] = useState(false); // New loading state
	const [isFolded, setIsFolded] = useState<boolean>(false);

	const onClickClose = () => {
		setIsFolded(!isFolded);
	};

	const doSearch = useCallback(() => {
		const { country, vendorName, vendorNumber, itemNumber, itemDescription } = localSearchParams;
		const newSearchParams: Partial<QueryParams> = {
			country: country && country.length > 0 ? country : [],
			vendorName: vendorName?.trim() ?? undefined,
			vendorNumber: vendorNumber !== undefined && vendorNumber !== null ? +vendorNumber : undefined,
			itemNumber: itemNumber !== undefined && itemNumber !== null ? +itemNumber : undefined,
			itemDescription: itemDescription?.trim() ?? undefined,
			commodity: commoditySelect.length > 0 ? commoditySelect.map((e) => e.toLowerCase()) : [],
		};
		// Pass freshly composed params directly to parent to avoid relying on parent's
		// searchParams state update timing. Parent will use overrideSubmittedParams when provided.
		updateSearchParams(newSearchParams);
		if (!shouldKeepExpanded) {
			setIsFolded(true); // Fold the search bar after search
		}
		handleSearchClick(newSearchParams); // Trigger the search action in the parent component with immediate params
	}, [
		localSearchParams,
		commoditySelect,
		shouldKeepExpanded,
		updateSearchParams,
		handleSearchClick,
	]);

	const doClear = () => {
		const newSearchParams: Partial<QueryParams> = {
			country: [],
			vendorName: undefined,
			vendorNumber: undefined,
			itemNumber: undefined,
			itemDescription: undefined,
			commodity: [],
		};
		setCommoditySelect([]);
		setLocalSearchParams(newSearchParams);
	};
	/* Start:  state control functions */
	const handleSelectCountry = (selected: string[]) => {
		setLocalSearchParams({ ...localSearchParams, country: selected });
	};

	// Handler to clear Country/Region selection
	const handleClearCountrySelection = useCallback(() => {
		setLocalSearchParams((prev) => ({ ...prev, country: [] }));
	}, []);

	const handleVendorNameChange = (event: ChangeEvent<HTMLInputElement>) => {
		const alphaNumericValue = validateStringInput(event.target.value, VENDOR_NAME_FIELD_LIMIT);
		setLocalSearchParams({
			...localSearchParams,
			vendorName: alphaNumericValue || undefined,
		});
	};
	const handleVendorNumberChange = (event: ChangeEvent<HTMLInputElement>) => {
		const numericValue = validateNumericInput(event.target.value, NUMERIC_FIELD_LIMIT);
		setLocalSearchParams({
			...localSearchParams,
			vendorNumber: numericValue || undefined,
		});
	};
	const handleItemNumberChange = (event: ChangeEvent<HTMLInputElement>) => {
		const numericValue = validateNumericInput(event.target.value, NUMERIC_FIELD_LIMIT);
		setLocalSearchParams({
			...localSearchParams,
			itemNumber: numericValue || undefined,
		});
	};
	const handleItemDescriptionChange = (event: ChangeEvent<HTMLInputElement>) => {
		const alphaNumericValue = validateStringInput(event.target.value, ITEM_DESCRIPTION_FIELD_LIMIT);
		setLocalSearchParams({
			...localSearchParams,
			itemDescription: alphaNumericValue ?? undefined,
		});
	};
	const handleCommodityChange = (selected: string[]) => {
		setCommoditySelect(selected);
	};

	// Handler to clear Commodity selection
	const handleClearCommoditySelection = useCallback(() => {
		setCommoditySelect([]);
	}, []);

	const handleClearField = (
		name: string,
		handler: (event: ChangeEvent<HTMLInputElement>) => void,
	) => {
		const clearEvent = {
			target: {
				value: "",
				name: name,
			},
		} as unknown as ChangeEvent<HTMLInputElement>; //unknown will explicitly allow us to assign any shape to clearEvent to satisfy different handler requirements

		handler(clearEvent);
	};

	const handleClearVendorName = () => {
		handleClearField("vendorName", handleVendorNameChange);
	};

	const handleClearVendorNumber = () => {
		handleClearField("vendorNumber", handleVendorNumberChange);
	};

	const handleClearItemDescription = () => {
		handleClearField("itemDescription", handleItemDescriptionChange);
	};

	const handleClearItemNumber = () => {
		handleClearField("itemNumber", handleItemNumberChange);
	};

	const handleKeyDown = (
		e: React.KeyboardEvent<HTMLInputElement | HTMLDivElement>,
		force?: boolean,
	) => {
		// The key that was pressed
		const key = e.key;

		// A regex to check if the key is a digit
		const isDigit = /^\d$/.test(key);

		// A list of allowed control keys
		const isControlKey = [
			"Backspace",
			"Delete",
			"ArrowLeft",
			"ArrowRight",
			"Tab",
			"Enter",
		].includes(key);

		// A check for copy/paste shortcuts
		const isShortcut = (e.ctrlKey || e.metaKey) && ["c", "v", "x", "a"].includes(key);

		// If force is true, only allow digits, control keys, and shortcuts
		if (force) {
			if (!(isDigit || isControlKey || isShortcut)) {
				e.preventDefault();
			}
		}
		// If the key is not a digit AND not a control key, block it
	};

	// Sync local commodity state with parent search parameters to prevent unnecessary re-renders
	useEffect(() => {
		const parentCommodity = searchParams.commodity ?? [];
		setCommoditySelect((prev) => {
			if (
				prev.length === parentCommodity.length &&
				prev.every((v, i) => v === parentCommodity[i])
			) {
				return prev; // no change, avoid re-render
			}
			return parentCommodity;
		});
	}, [searchParams.commodity]);

	useEffect(() => {
		const { itemDescription, itemNumber, vendorNumber, vendorName, country } = localSearchParams;
		const isFieldNotEmpty = (value: number | string | string[] | undefined) => {
			if (Array.isArray(value)) {
				return value.length > 0;
			}
			return value && value.toString().trim() !== "";
		};
		const searchAllowStatus = [
			itemDescription,
			itemNumber,
			vendorNumber,
			vendorName,
			country,
			commoditySelect,
		].some(isFieldNotEmpty);
		setIsSearchButtonEnabled(searchAllowStatus);
		searchCardExpanded?.(searchAllowStatus);
	}, [commoditySelect, localSearchParams, searchCardExpanded]);
	// End: state control functions

	// This ref is used to ensure that the commodity API is called only once
	const didCommodityGotCalled = useRef(false);

	useEffect(() => {
		const storedCommodityArrayList = sessionStorage.getItem("commodityArrayListStored");
		if (storedCommodityArrayList) {
			setCommodityList(JSON.parse(storedCommodityArrayList) as Option[]);
		}
		if (!didCommodityGotCalled.current) {
			setIsLoading(true); // Start loading
			CommodityApi.getCommodityList()
				.then((list: Option[]) => {
					setCommodityList(list || []);
				})
				.catch(() => {
					setCommodityList([]);
				})
				.finally(() => {
					setIsLoading(false);
				});
			didCommodityGotCalled.current = true;
		}
	}, []);

	// Notify parent of doSearch function
	useEffect(() => {
		onDoSearchChange?.(doSearch);
	}, [doSearch, onDoSearchChange]);

	useEffect(() => {
		if (shouldKeepExpanded) {
			setIsFolded(false);
		}
	}, [shouldKeepExpanded]);

	// Handle Enter key press on Clear All button
	const handleClearKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
		if (e.key === "Enter" && isSearchButtonEnabled) {
			e.stopPropagation(); // Prevent the event from bubbling up
			doClear();
		}
	};

	return (
		<div
			className={`h-auto mt-5 mx-5 shadow-md border-1 rounded-md border-gray-200 relative ${!isFolded && "pb-6"}`}
		>
			<button
				type="button"
				onClick={onClickClose}
				className={`flex flex-row justify-between w-full pb-4 pt-6 transition transition-discrete delay-300 duration-300 ease-in-out hover:bg-blue-600/5  ${!isFolded && "bg-blue-500/5"}`}
			>
				<div className="text-blue-600 justify-items-start font-semibold text-lg pl-8">
					<span>SEARCH BY</span>
				</div>
				<button
					type="button"
					className="justify-items-end font-bold pr-10 flex flex-row text-blue-600 cursor-pointer"
					data-testid="search-bar-toggle-button"
				>
					{isFolded && <div className="pr-6 font-normal">Edit Search</div>}
					<span
						className={`transition-discrete font-bold ${isFolded ? "text-2xl icon-expand_more" : "text-2xl icon-expand_less"} `}
					/>
				</button>
			</button>

			<div className="flex flex-col w-full">
				<div
					className={`flex flex-col w-full px-5 flex-wrap min-[1000px]:flex-row min-[800px]:flex-nowrap ${isFolded ? "hidden" : "pt-4"} gap-2`}
				>
					<div className={`${SellingCountryDropdownStyle} pr-2`}>
						<InputLabel
							id="input-label-searchbar-countryregion"
							label={SEARCH_LABELS.COUNTRYREGION}
							className="ml-2 truncate max-w-[180px]"
							htmlFor="country-region-select"
						/>
						<Select
							id="country-region-select"
							dataTestID="searchbar-country-region-select"
							optionList={multiSelectCountryRegion}
							typeOfField={SEARCH_LABELS.COUNTRYREGION}
							onSelect={handleSelectCountry}
							onKeyDown={handleKeyDown}
							valueOfField={localSearchParams.country || []}
							handleSelectNone={handleClearCountrySelection}
						/>
					</div>
					<StyledInput
						id="Vendor #"
						type="number"
						className={`${commonInputStyle} pr-0.5 [@media(min-width:800px) and (max-width:1500px)]:pl-[15px]`}
						value={localSearchParams.vendorNumber ?? ""}
						onKeyDown={(e) => handleKeyDown(e, true)}
						onChange={handleVendorNumberChange}
						onClear={handleClearVendorNumber}
					/>
					<StyledInput
						id="Vendor Name"
						type="text"
						className={commonGrowInputStyle}
						value={localSearchParams.vendorName}
						onKeyDown={handleKeyDown}
						onChange={handleVendorNameChange}
						onClear={handleClearVendorName}
					/>
					<StyledInput
						id="Item #"
						type="number"
						className={commonInputStyle}
						value={localSearchParams.itemNumber ?? ""}
						onKeyDown={(e) => handleKeyDown(e, true)}
						onChange={handleItemNumberChange}
						onClear={handleClearItemNumber}
					/>
					<StyledInput
						id="Item Description"
						type="text"
						className={commonGrowInputStyle}
						value={localSearchParams.itemDescription}
						onKeyDown={handleKeyDown}
						onChange={handleItemDescriptionChange}
						onClear={handleClearItemDescription}
					/>
					<div className={`${commonDropdownStyle} pr-2`}>
						<InputLabel
							id="input-label-searchbar-commodity"
							label={SEARCH_LABELS.COMMODITY}
							className="ml-2"
							htmlFor="commodity-select"
						/>
						<Select
							id="commodity-select"
							dataTestID="searchbar-commodity-select"
							optionList={commodityList}
							typeOfField={SEARCH_LABELS.COMMODITY}
							onSelect={handleCommodityChange}
							onKeyDown={handleKeyDown}
							valueOfField={commoditySelect}
							isLoading={isLoading}
							handleSelectNone={handleClearCommoditySelection}
						/>
					</div>
				</div>
				{!isFolded && (
					<div className="pb-2 flex flex-row-reverse w-full pr-10">
						<StyledButton
							disabled={!isSearchButtonEnabled}
							id="searchbar-search-button"
							text={"Search"}
							onClick={doSearch}
							className={searchButtonStyle}
						/>
						<button
							type="button"
							disabled={!isSearchButtonEnabled}
							id="searchbar-clear-button"
							onClick={doClear}
							onKeyDown={handleClearKeyDown}
							className={`mx-4 flex flex-row items-center justify-center text-blue-600 font-bold h-[48px]  ${clearAllButtonStyle} ${
								isSearchButtonEnabled ? "cursor-pointer" : "hidden"
							}`}
						>
							<div className="inline-block icon-close text-2xl pr-2" />
							<p className="text-base font-normal">Clear All</p>
						</button>
					</div>
				)}
			</div>
		</div>
	);
};
export default SearchForm;
